import { effects as gfxBrushEffects } from '@blocksuite/hive-gfx-brush/effects';
import { effects as gfxConnectorEffects } from '@blocksuite/hive-gfx-connector/effects';
import { effects as gfxGroupEffects } from '@blocksuite/hive-gfx-group/effects';
import { effects as gfxMindmapEffects } from '@blocksuite/hive-gfx-mindmap/effects';
import { effects as gfxNoteEffects } from '@blocksuite/hive-gfx-note/effects';
import { effects as gfxShapeEffects } from '@blocksuite/hive-gfx-shape/effects';
import { effects as gfxTemplateEffects } from '@blocksuite/hive-gfx-template/effects';
import { effects as gfxCanvasTextEffects } from '@blocksuite/hive-gfx-text/effects';
import { effects as widgetEdgelessToolbarEffects } from '@blocksuite/hive-widget-edgeless-toolbar/effects';
import { effects as widgetMobileToolbarEffects } from '@blocksuite/hive-widget-keyboard-toolbar/effects';
import { effects as widgetLinkedDocEffects } from '@blocksuite/hive-widget-linked-doc/effects';

import { EdgelessAutoCompletePanel } from './edgeless/components/auto-complete/auto-complete-panel.js';
import { EdgelessAutoComplete } from './edgeless/components/auto-complete/edgeless-auto-complete.js';
import {
  NOTE_SLICER_WIDGET,
  NoteSlicer,
} from './edgeless/components/note-slicer/index.js';
import {
  EDGELESS_DRAGGING_AREA_WIDGET,
  EdgelessDraggingAreaRectWidget,
} from './edgeless/components/rects/edgeless-dragging-area-rect.js';
import {
  EDGELESS_SELECTED_RECT_WIDGET,
  EdgelessSelectedRectWidget,
} from './edgeless/components/rects/edgeless-selected-rect.js';
import { EdgelessSlideMenu } from './edgeless/components/toolbar/common/slide-menu.js';
import { ToolbarArrowUpIcon } from './edgeless/components/toolbar/common/toolbar-arrow-up-icon.js';
import { EdgelessDefaultToolButton } from './edgeless/components/toolbar/default/default-tool-button.js';
import { EdgelessLinkToolButton } from './edgeless/components/toolbar/link/link-tool-button.js';
import {
  EdgelessRootBlockComponent,
  EdgelessRootPreviewBlockComponent,
  PageRootBlockComponent,
  PreviewRootBlockComponent,
} from './index.js';
import {
  EDGELESS_NAVIGATOR_BLACK_BACKGROUND_WIDGET,
  EdgelessNavigatorBlackBackgroundWidget,
} from './widgets/edgeless-navigator-bg/index.js';
import {
  HIVE_EDGELESS_ZOOM_TOOLBAR_WIDGET,
  HiveEdgelessZoomToolbarWidget,
} from './widgets/edgeless-zoom-toolbar/index.js';
import { ZoomBarToggleButton } from './widgets/edgeless-zoom-toolbar/zoom-bar-toggle-button.js';
import { EdgelessZoomToolbar } from './widgets/edgeless-zoom-toolbar/zoom-toolbar.js';
import {
  HIVE_PAGE_DRAGGING_AREA_WIDGET,
  HivePageDraggingAreaWidget,
} from './widgets/page-dragging-area/page-dragging-area.js';
import {
  HIVE_VIEWPORT_OVERLAY_WIDGET,
  HiveViewportOverlayWidget,
} from './widgets/viewport-overlay/viewport-overlay.js';

export function effects() {
  // Run other effects
  widgetMobileToolbarEffects();
  widgetLinkedDocEffects();
  widgetEdgelessToolbarEffects();

  // Register components by category
  registerRootComponents();
  registerGfxEffects();
  registerWidgets();
  registerEdgelessToolbarComponents();
  registerMiscComponents();
}

function registerRootComponents() {
  customElements.define('hive-page-root', PageRootBlockComponent);
  customElements.define('hive-preview-root', PreviewRootBlockComponent);
  customElements.define('hive-edgeless-root', EdgelessRootBlockComponent);
  customElements.define(
    'hive-edgeless-root-preview',
    EdgelessRootPreviewBlockComponent
  );
}

function registerGfxEffects() {
  gfxCanvasTextEffects();
  gfxShapeEffects();
  gfxNoteEffects();
  gfxConnectorEffects();
  gfxMindmapEffects();
  gfxGroupEffects();
  gfxBrushEffects();
  gfxTemplateEffects();
}

function registerWidgets() {
  customElements.define(
    HIVE_PAGE_DRAGGING_AREA_WIDGET,
    HivePageDraggingAreaWidget
  );
  customElements.define(
    HIVE_VIEWPORT_OVERLAY_WIDGET,
    HiveViewportOverlayWidget
  );
  customElements.define(
    HIVE_EDGELESS_ZOOM_TOOLBAR_WIDGET,
    HiveEdgelessZoomToolbarWidget
  );
}

function registerEdgelessToolbarComponents() {
  // Tool buttons
  customElements.define(
    'edgeless-default-tool-button',
    EdgelessDefaultToolButton
  );
  customElements.define('edgeless-link-tool-button', EdgelessLinkToolButton);

  // Menus
  customElements.define('edgeless-slide-menu', EdgelessSlideMenu);

  // Toolbar components
  customElements.define('toolbar-arrow-up-icon', ToolbarArrowUpIcon);
}

function registerMiscComponents() {
  // Toolbar and UI components
  customElements.define('edgeless-zoom-toolbar', EdgelessZoomToolbar);
  customElements.define('zoom-bar-toggle-button', ZoomBarToggleButton);

  // Auto-complete components
  customElements.define(
    'edgeless-auto-complete-panel',
    EdgelessAutoCompletePanel
  );
  customElements.define('edgeless-auto-complete', EdgelessAutoComplete);

  // Note and template components
  customElements.define(NOTE_SLICER_WIDGET, NoteSlicer);

  // Navigation components
  customElements.define(
    EDGELESS_NAVIGATOR_BLACK_BACKGROUND_WIDGET,
    EdgelessNavigatorBlackBackgroundWidget
  );

  // Dragging area components
  customElements.define(
    EDGELESS_DRAGGING_AREA_WIDGET,
    EdgelessDraggingAreaRectWidget
  );
  customElements.define(
    EDGELESS_SELECTED_RECT_WIDGET,
    EdgelessSelectedRectWidget
  );
}

declare global {
  interface HTMLElementTagNameMap {
    'hive-edgeless-root': EdgelessRootBlockComponent;
    'hive-edgeless-root-preview': EdgelessRootPreviewBlockComponent;
    'edgeless-auto-complete-panel': EdgelessAutoCompletePanel;
    'edgeless-auto-complete': EdgelessAutoComplete;
    'note-slicer': NoteSlicer;
    'edgeless-navigator-black-background': EdgelessNavigatorBlackBackgroundWidget;
    'edgeless-dragging-area-rect': EdgelessDraggingAreaRectWidget;
    'edgeless-selected-rect': EdgelessSelectedRectWidget;
    'edgeless-slide-menu': EdgelessSlideMenu;
    'toolbar-arrow-up-icon': ToolbarArrowUpIcon;
    'edgeless-default-tool-button': EdgelessDefaultToolButton;
    'edgeless-link-tool-button': EdgelessLinkToolButton;
    'hive-page-root': PageRootBlockComponent;
    'zoom-bar-toggle-button': ZoomBarToggleButton;
    'edgeless-zoom-toolbar': EdgelessZoomToolbar;

    [HIVE_EDGELESS_ZOOM_TOOLBAR_WIDGET]: HiveEdgelessZoomToolbarWidget;
  }
}
